// SPDX-License-Identifier: AGPL-3.0-or-later

import {msg} from '@lingui/core/macro';

export const LOBBY_COPY = {
	title: msg({
		message: 'Find a game friend',
		context: 'lobby',
		comment: 'Lobby discovery page title. Matches DM sidebar label.',
	}),
	subtitle: msg({
		message: 'Find your team and play together.',
		context: 'lobby',
		comment: 'Lobby discovery page subtitle.',
	}),
	heroBrowseHint: msg({
		message: 'Pick a game below, or jump into a lobby that is already looking for players.',
		context: 'lobby',
		comment: 'Short helper under the discovery hero title.',
	}),
	liveNow: msg({
		message: 'Live',
		context: 'lobby',
		comment: 'Small live badge on the lobby discovery hero. Keep it concise.',
	}),
	openLobbiesStat: msg({
		message: 'Open lobbies',
		context: 'lobby',
		comment: 'Discovery hero stat label under the open-lobby count.',
	}),
	activeGamesStat: msg({
		message: 'Games live',
		context: 'lobby',
		comment: 'Discovery hero stat label under the games-with-lobbies count. Keep it concise.',
	}),
	browseGamesHint: msg({
		message: 'Tap a game to filter the list. Tap again to clear.',
		context: 'lobby',
		comment: 'Helper under the popular games heading.',
	}),
	seeOpenLobbies: msg({
		message: 'See open lobbies',
		context: 'lobby',
		comment: 'Secondary hero action that scrolls to the active lobby list.',
	}),
	emptyLobbiesHint: msg({
		message: 'Be the first to open a session for your game.',
		context: 'lobby',
		comment: 'Secondary empty-state line under the main empty lobbies message.',
	}),
	detailReadyHint: msg({
		message: 'Coordinate here, then jump in-game.',
		context: 'lobby',
		comment: 'Short helper on the lobby interior hero for members.',
	}),
	createLobby: msg({
		message: 'Create lobby',
		context: 'lobby',
		comment: 'Primary action to open the create-lobby modal. Keep it concise.',
	}),
	searchPlaceholder: msg({
		message: 'Search games or lobbies…',
		context: 'lobby',
		comment: 'Search input placeholder on lobby discovery.',
	}),
	categoryAll: msg({
		message: 'All',
		context: 'lobby',
		comment: 'Lobby game category chip. Keep it concise.',
	}),
	categoryFps: msg({
		message: 'FPS',
		context: 'lobby',
		comment: 'Lobby game category chip. Keep it concise.',
	}),
	categoryMoba: msg({
		message: 'MOBA',
		context: 'lobby',
		comment: 'Lobby game category chip. Keep it concise.',
	}),
	categoryBattleRoyale: msg({
		message: 'Battle Royale',
		context: 'lobby',
		comment: 'Lobby game category chip.',
	}),
	categoryOther: msg({
		message: 'Other',
		context: 'lobby',
		comment: 'Lobby game category chip. Keep it concise.',
	}),
	clearFilters: msg({
		message: 'Clear filters',
		context: 'lobby',
		comment: 'Button on lobby discovery that resets search, category, game and filters. Keep it concise.',
	}),
	popularGames: msg({
		message: 'Popular games',
		context: 'lobby',
		comment: 'Section heading above the game grid.',
	}),
	activeLobbies: msg({
		message: 'Active lobbies',
		context: 'lobby',
		comment: 'Section heading above the lobby card list.',
	}),
	openLobbiesCount: msg({
		message: '{count, plural, one {# open lobby} other {# open lobbies}}',
		context: 'lobby',
		comment: 'Active lobby count under a game card. count is the number of open lobbies.',
	}),
	emptyLobbies: msg({
		message: 'No open lobbies right now. Create the first one.',
		context: 'lobby',
		comment: 'Empty state when discovery has no active lobbies.',
	}),
	emptyGameLobbies: msg({
		message: 'No open lobbies for this game.',
		context: 'lobby',
		comment: 'Empty state when a game filter returns no lobbies.',
	}),
	loading: msg({
		message: 'Loading…',
		context: 'lobby',
		comment: 'Generic loading state for lobby screens.',
	}),
	loadError: msg({
		message: 'Could not load lobbies.',
		context: 'lobby',
		comment: 'Lobby discovery error state.',
	}),
	retry: msg({
		message: 'Try again',
		context: 'lobby',
		comment: 'Retry loading lobbies. Keep it concise.',
	}),
	backToLobbies: msg({
		message: 'Back to lobbies',
		context: 'lobby',
		comment: 'Navigation link from lobby detail back to discovery.',
	}),
	statusOpen: msg({
		message: 'Open',
		context: 'lobby',
		comment: 'Lobby status badge. Keep it concise.',
	}),
	statusFull: msg({
		message: 'Full',
		context: 'lobby',
		comment: 'Lobby status badge when at capacity.',
	}),
	statusPending: msg({
		message: 'Pending',
		context: 'lobby',
		comment: 'Lobby status when viewer has a pending join request.',
	}),
	statusMember: msg({
		message: 'Joined',
		context: 'lobby',
		comment: 'Lobby status when viewer is already a member.',
	}),
	statusClosed: msg({
		message: 'Closed',
		context: 'lobby',
		comment: 'Lobby status when closed by owner.',
	}),
	statusExpired: msg({
		message: 'Expired',
		context: 'lobby',
		comment: 'Lobby status when past expires_at.',
	}),
	join: msg({
		message: 'Join lobby',
		context: 'lobby',
		comment: 'CTA to join an open lobby. Keep it concise.',
	}),
	requestJoin: msg({
		message: 'Request to join',
		context: 'lobby',
		comment: 'CTA to request joining an approval lobby.',
	}),
	pendingApproval: msg({
		message: 'Pending approval',
		context: 'lobby',
		comment: 'Disabled CTA while join request awaits owner.',
	}),
	joined: msg({
		message: 'Joined',
		context: 'lobby',
		comment: 'Disabled CTA when viewer is already a member.',
	}),
	lobbyFull: msg({
		message: 'Lobby full',
		context: 'lobby',
		comment: 'Disabled CTA when lobby has no slots.',
	}),
	lobbyClosed: msg({
		message: 'Lobby closed',
		context: 'lobby',
		comment: 'Disabled CTA when lobby is closed or expired.',
	}),
	leave: msg({
		message: 'Leave lobby',
		context: 'lobby',
		comment: 'Action for non-owner members to leave.',
	}),
	closeLobby: msg({
		message: 'Close lobby',
		context: 'lobby',
		comment: 'Owner action to close the lobby.',
	}),
	closeLobbyConfirmTitle: msg({
		message: 'Close this lobby?',
		context: 'lobby',
		comment: 'Confirm modal title before closing a lobby.',
	}),
	closeLobbyConfirmBody: msg({
		message: 'Are you sure you want to close this lobby? Members will no longer be able to join or chat.',
		context: 'lobby',
		comment: 'Confirm modal body before closing a lobby.',
	}),
	adminCloseLobby: msg({
		message: 'Close lobby (moderator)',
		context: 'lobby',
		comment: 'Moderator action to close any lobby. Keep it concise.',
	}),
	adminCloseLobbyConfirmBody: msg({
		message: 'This closes the lobby for everyone, even if you are not the owner.',
		context: 'lobby',
		comment: 'Confirm modal body for moderator lobby close.',
	}),
	createRestricted: msg({
		message: 'You are not allowed to create lobbies.',
		context: 'lobby',
		comment: 'Shown when a restricted user tries to create a lobby.',
	}),
	restrictLobbyCreate: msg({
		message: 'Restrict lobby creation',
		context: 'lobby',
		comment: 'Moderator context-menu action to block a user from creating lobbies.',
	}),
	restrictLobbyCreateDone: msg({
		message: '{name} can no longer create lobbies.',
		context: 'lobby',
		comment: 'Toast after restricting lobby creation. {name} is display name.',
	}),
	allowLobbyCreate: msg({
		message: 'Allow lobby creation',
		context: 'lobby',
		comment: 'Moderator context-menu action to restore lobby creation for a user.',
	}),
	allowLobbyCreateDone: msg({
		message: '{name} can create lobbies again.',
		context: 'lobby',
		comment: 'Toast after restoring lobby creation. {name} is display name.',
	}),
	redirectedClosed: msg({
		message: 'This lobby was closed.',
		context: 'lobby',
		comment: 'Toast when the viewer is redirected because the lobby closed.',
	}),
	editLobby: msg({
		message: 'Edit lobby',
		context: 'lobby',
		comment: 'Owner action to edit lobby settings.',
	}),
	ownerBadge: msg({
		message: 'Lobby owner',
		context: 'lobby',
		comment: 'Badge next to the lobby owner in the member list.',
	}),
	members: msg({
		message: 'Members',
		context: 'lobby',
		comment: 'Section heading for lobby member list.',
	}),
	chat: msg({
		message: 'Chat',
		context: 'lobby',
		comment: 'Section heading for lobby chat.',
	}),
	chatPlaceholder: msg({
		message: 'Message the lobby…',
		context: 'lobby',
		comment: 'Lobby chat composer placeholder.',
	}),
	chatMembersOnly: msg({
		message: 'Join the lobby to chat.',
		context: 'lobby',
		comment: 'Shown when non-members view lobby chat.',
	}),
	chatEmpty: msg({
		message: 'No messages yet. Send the first one and rally your team.',
		context: 'lobby',
		comment: 'Empty lobby chat state.',
	}),
	joinRequests: msg({
		message: 'Join requests',
		context: 'lobby',
		comment: 'Owner section heading for pending join requests.',
	}),
	approve: msg({
		message: 'Approve',
		context: 'lobby',
		comment: 'Approve a join request. Keep it concise.',
	}),
	reject: msg({
		message: 'Reject',
		context: 'lobby',
		comment: 'Reject a join request. Keep it concise.',
	}),
	noJoinRequests: msg({
		message: 'No pending requests.',
		context: 'lobby',
		comment: 'Empty state for join requests list.',
	}),
	kick: msg({
		message: 'Remove from lobby',
		context: 'lobby',
		comment: 'Owner action to kick a member.',
	}),
	kickConfirmTitle: msg({
		message: 'Remove {name}?',
		context: 'lobby',
		comment: 'Confirm modal title before kicking a member. {name} is display name.',
	}),
	kickConfirmBody: msg({
		message: 'They will lose access to lobby chat immediately.',
		context: 'lobby',
		comment: 'Confirm modal body before kicking a member.',
	}),
	inviteToServer: msg({
		message: 'Invite to server',
		context: 'lobby',
		comment: 'Owner action to invite a lobby member to a guild.',
	}),
	inviteModalTitle: msg({
		message: 'Invite {name} to a server',
		context: 'lobby',
		comment: 'Invite modal title. {name} is the target display name.',
	}),
	inviteModalSubtitle: msg({
		message: 'Choose a server you can create invites for.',
		context: 'lobby',
		comment: 'Invite modal helper text.',
	}),
	inviteSend: msg({
		message: 'Send invite',
		context: 'lobby',
		comment: 'Confirm sending a guild invite from lobby.',
	}),
	inviteEmptyGuilds: msg({
		message: 'No servers available to invite from.',
		context: 'lobby',
		comment: 'Empty state when owner has no invite-capable guilds.',
	}),
	inviteSuccess: msg({
		message: 'Invite sent.',
		context: 'lobby',
		comment: 'Toast after successfully inviting a lobby member to a guild.',
	}),
	remainingTime: msg({
		message: '{hours}h {minutes}m left',
		context: 'lobby',
		comment: 'Countdown until lobby expires. hours and minutes are remaining time parts.',
	}),
	remainingMinutes: msg({
		message: '{minutes}m left',
		context: 'lobby',
		comment: 'Countdown under one hour until lobby expires.',
	}),
	remainingExpired: msg({
		message: 'Expired',
		context: 'lobby',
		comment: 'Shown when lobby expires_at is in the past.',
	}),
	micRequired: msg({
		message: 'Mic required',
		context: 'lobby',
		comment: 'Chip when microphone is required.',
	}),
	micOptional: msg({
		message: 'Mic optional',
		context: 'lobby',
		comment: 'Chip when microphone is optional.',
	}),
	micValueRequired: msg({
		message: 'Required',
		context: 'lobby',
		comment: 'Short microphone value used after "Microphone:" on lobby cards.',
	}),
	micValueOptional: msg({
		message: "Doesn't matter",
		context: 'lobby',
		comment: 'Short microphone value used after "Microphone:" on lobby cards.',
	}),
	playCompetitive: msg({
		message: 'Competitive',
		context: 'lobby',
		comment: 'Play style chip.',
	}),
	playCasual: msg({
		message: 'Casual',
		context: 'lobby',
		comment: 'Play style chip.',
	}),
	playAny: msg({
		message: "Doesn't matter",
		context: 'lobby',
		comment: 'Play style chip for any. Shown after "Play style:" on cards.',
	}),
	filterLanguage: msg({
		message: 'Language',
		context: 'lobby',
		comment: 'Filter label for lobby language.',
	}),
	filterMicrophone: msg({
		message: 'Microphone',
		context: 'lobby',
		comment: 'Filter label for microphone preference.',
	}),
	filterPlayStyle: msg({
		message: 'Play style',
		context: 'lobby',
		comment: 'Filter label for play style.',
	}),
	filterHasSlots: msg({
		message: 'Has open slots',
		context: 'lobby',
		comment: 'Toggle filter for lobbies with available seats.',
	}),
	filterAny: msg({
		message: 'Any',
		context: 'lobby',
		comment: 'Clear/any option in lobby filters.',
	}),
	activeLobbyCount: msg({
		message: '{count, plural, one {# lobby} other {# lobbies}}',
		context: 'lobby',
		comment: 'Active lobby count beside section heading.',
	}),
	openLobby: msg({
		message: 'Open lobby',
		context: 'lobby',
		comment: 'CTA for owners/members to open lobby detail from a card.',
	}),
	playersHeading: msg({
		message: 'Players',
		context: 'lobby',
		comment: 'Members rail heading on lobby detail.',
	}),
	lobbyInfoHeading: msg({
		message: 'Lobby info',
		context: 'lobby',
		comment: 'Side panel heading with lobby metadata on detail page.',
	}),
	lookingForPlayer: msg({
		message: 'Looking for player',
		context: 'lobby',
		comment: 'Empty seat row in the players rail.',
	}),
	infoPlayers: msg({
		message: 'Players',
		context: 'lobby',
		comment: 'Lobby info row label for member count.',
	}),
	infoTimeLeft: msg({
		message: 'Time left',
		context: 'lobby',
		comment: 'Lobby info row label for remaining time.',
	}),
	infoCreated: msg({
		message: 'Created',
		context: 'lobby',
		comment: 'Lobby info row label for created time.',
	}),
	infoGame: msg({
		message: 'Game',
		context: 'lobby',
		comment: 'Lobby info row label for game name.',
	}),
	infoLanguage: msg({
		message: 'Language',
		context: 'lobby',
		comment: 'Lobby info row label for language.',
	}),
	infoMicrophone: msg({
		message: 'Microphone',
		context: 'lobby',
		comment: 'Lobby info row label for microphone preference.',
	}),
	infoPlayStyle: msg({
		message: 'Play style',
		context: 'lobby',
		comment: 'Lobby info row label for play style.',
	}),
	slotsNeeded: msg({
		message: '{count, plural, one {# more player needed} other {# more players needed}}',
		context: 'lobby',
		comment: 'Empty slot hint in members rail. count is remaining seats.',
	}),
	lobbyChatHeading: msg({
		message: 'Lobby chat',
		context: 'lobby',
		comment: 'Chat panel heading on lobby detail.',
	}),
	lobbySettings: msg({
		message: 'Lobby settings',
		context: 'lobby',
		comment: 'Accessible label for owner overflow menu.',
	}),
	createdAgo: msg({
		message: 'Created {relative}',
		context: 'lobby',
		comment: 'Relative created time. relative is a short relative time string.',
	}),
	ownedBy: msg({
		message: 'Hosted by {name}',
		context: 'lobby',
		comment: 'Owner attribution on lobby hero. name is display name.',
	}),
	createModalTitle: msg({
		message: 'Create lobby',
		context: 'lobby',
		comment: 'Create lobby modal title.',
	}),
	editModalTitle: msg({
		message: 'Edit lobby',
		context: 'lobby',
		comment: 'Edit lobby modal title.',
	}),
	fieldGame: msg({
		message: 'Game',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldCustomGame: msg({
		message: 'Game name',
		context: 'lobby',
		comment: 'Custom game name when Other is selected.',
	}),
	fieldTitle: msg({
		message: 'Lobby title',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldDescription: msg({
		message: 'Description',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldLanguage: msg({
		message: 'Language',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldMicrophone: msg({
		message: 'Microphone',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldPlayStyle: msg({
		message: 'Play style',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldMaxMembers: msg({
		message: 'Max players',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldJoinPolicy: msg({
		message: 'Join policy',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldDuration: msg({
		message: 'Duration',
		context: 'lobby',
		comment: 'Create lobby form label.',
	}),
	fieldSelectPlaceholder: msg({
		message: 'Select…',
		context: 'lobby',
		comment: 'Placeholder for required create-lobby selects before a value is chosen.',
	}),
	joinOpen: msg({
		message: 'Open to everyone',
		context: 'lobby',
		comment: 'Join policy option.',
	}),
	joinApproval: msg({
		message: 'Requires approval',
		context: 'lobby',
		comment: 'Join policy option.',
	}),
	durationOneHour: msg({
		message: '1 hour',
		context: 'lobby',
		comment: 'Lobby duration option.',
	}),
	durationTwoHours: msg({
		message: '2 hours',
		context: 'lobby',
		comment: 'Lobby duration option.',
	}),
	durationFourHours: msg({
		message: '4 hours',
		context: 'lobby',
		comment: 'Lobby duration option.',
	}),
	save: msg({
		message: 'Save',
		context: 'lobby',
		comment: 'Save lobby edits. Keep it concise.',
	}),
	cancel: msg({
		message: 'Cancel',
		context: 'lobby',
		comment: 'Cancel modal action. Keep it concise.',
	}),
	create: msg({
		message: 'Create',
		context: 'lobby',
		comment: 'Submit create lobby. Keep it concise.',
	}),
	send: msg({
		message: 'Send',
		context: 'lobby',
		comment: 'Send lobby chat message. Keep it concise.',
	}),
	loadOlder: msg({
		message: 'Load older messages',
		context: 'lobby',
		comment: 'Load older lobby chat history.',
	}),
	actionFailed: msg({
		message: 'Something went wrong. Try again.',
		context: 'lobby',
		comment: 'Generic lobby mutation error toast.',
	}),
	detailLoadError: msg({
		message: 'Could not load this lobby.',
		context: 'lobby',
		comment: 'Lobby detail error state.',
	}),
	expiredNotice: msg({
		message: 'This lobby has expired.',
		context: 'lobby',
		comment: 'Notice on detail when lobby is expired.',
	}),
	closedNotice: msg({
		message: 'This lobby has been closed.',
		context: 'lobby',
		comment: 'Notice on detail when lobby is closed.',
	}),
	memberCount: msg({
		message: '{current}/{max}',
		context: 'lobby',
		comment: 'Member count fraction display. current and max are numbers.',
	}),
	messageCount: msg({
		message: '{count, plural, one {# message} other {# messages}}',
		context: 'lobby',
		comment: 'Message count on lobby cards.',
	}),
} as const;
